import { useSetState, useUpdateEffect } from 'ahooks'
import { Checkbox } from 'antd-mobile'
import { RightOutline } from 'antd-mobile-icons'
import classNames from 'classnames'
import { throttle, cloneDeep } from 'lodash-es'
import { observer } from 'mobx-react-lite'
import React, { useEffect, Fragment, useMemo } from 'react'

import { HSearchView, HTreeNode } from '@/components/NewEditForm/UILibrary/HYBVant'
import { SELECT_MODE } from '@/constants'
import { dealTreeData, uniqueItems } from '@/utils'

interface TreeSearchViewProps {
  treeApiMap: Record<string, (params: any) => Promise<any>>
  searchParams: any
  mode?: 'multiple' | 'single' | 'view'
  value?: any[]
  fieldNames?: any
  onConfirm?: (employee: any[]) => void
  [key: string]: any
}

const TreeSearchView: React.FC<TreeSearchViewProps> = (props) => {
  const {
    treeApiMap,
    searchParams = {},
    needAuth = true,
    mode = 'single',
    value,
    fieldNames = { children: 'children' },
    onConfirm
  } = props

  const [
    {
      allCount,
      keyword,
      originTreeData,
      treeData,
      currentTreeItem,
      allSelected,
      selectedList,
      loaded
    },
    setState
  ] = useSetState<any>({
    allCount: 1,
    loaded: false,
    keyword: '',
    selectedList: [],
    originTreeData: [],
    treeData: [],
    currentTreeItem: [],
    allSelected: false
  })

  // 是否可选择
  const selectable = mode !== 'view'

  // 单选
  const isSingle = mode === SELECT_MODE.SINGLE

  const onSearchChange = (keyword: string) => {
    setState({ keyword })
  }

  // 面包屑
  const breadcrumbList = useMemo(() => {
    const breadcrumbs = []
    const tempData = [
      {
        name: '回到顶部',
        id: 'top',
        childs: cloneDeep(originTreeData)
      }
    ]
    const findParent = (data: any[], target: any): any[] => {
      for (const item of data) {
        if (item.id === target.id) {
          return [item]
        }
        if (item.childs) {
          const found = findParent(item.childs, target)
          if (found.length) {
            return [item, ...found]
          }
        }
      }
      return []
    }

    if (originTreeData?.length && currentTreeItem?.id) {
      breadcrumbs.push(...findParent(tempData, currentTreeItem))
    }

    return breadcrumbs
  }, [originTreeData, currentTreeItem])

  // 获取树结构节点的个数包含当前节点
  const getTreeCount = (data: any[]) => {
    return data.reduce((acc, item) => {
      acc += 1
      if (item.childs) {
        acc += getTreeCount(item.childs)
      }
      return acc
    }, 0)
  }

  // 获取机构
  const queryTreeData = async () => {
    const { data } = await treeApiMap.Query(searchParams)
    // children => childs
    const treeData = dealTreeData(data, fieldNames?.children)

    setState({
      loaded: true,
      originTreeData: cloneDeep(treeData),
      treeData,
      currentTreeItem: {
        name: '回到顶部',
        id: 'top',
        childs: treeData
      },
      allCount: getTreeCount(treeData) || 1
    })
  }

  // 组织点击
  const handleOrgClick = (item: any) => {
    setState({
      allSelected: false,
      currentTreeItem: item,
      treeData: item?.childs || []
    })
  }

  // 获取数据
  const queryTreeDataByKeyword = async (params: any) => {
    const { data } = await treeApiMap.Search({
      sub_flag: true,
      paged: false,
      query_deleted: false,
      ...searchParams,
      ...params
    })
    return data?.map((x: any) => ({ ...x, item_desc: x?.org_names?.join?.('/') }))
  }
  // 组织选择
  const handleTreeChange = (val: any, list?: any[]) => {
    setState({ selectedList: list })
  }

  // 移除
  const onRemove = (item: any, list: any[]) => {
    setState({ selectedList: list })
  }

  // 递归获取当前节点的所有子节点id
  const getChildIds = (data: any[]) => {
    return data.reduce((acc, item) => {
      acc.push(item.id)
      if (item.childs) {
        acc.push(...getChildIds(item.childs))
      }
      return acc
    }, [])
  }

  // 选中人员的值
  const tempValue = useMemo(() => {
    if (isSingle) {
      return selectedList?.[0]?.id || null
    }
    return selectedList?.map((x: any) => x?.id)
  }, [selectedList, mode])

  // 将第二个数组中的数据在第一个数组中去除
  const removeData = (data: any[], removeData: any[]) => {
    return data.filter((x) => !removeData.some((y) => y.id === x.id))
  }

  // 关键字变动
  useUpdateEffect(() => {
    if (keyword) {
      queryTreeDataByKeyword({ keyword }).then((list: any) => {
        setState({ treeData: list })
      })
    } else {
      setState({ treeData: originTreeData })
    }
  }, [keyword])

  useEffect(() => {
    queryTreeData()
  }, [])

  // 组织选择
  const handleTreeItemChange = async (flag: boolean) => {
    let tempSelectList = []
    // 最后一级或者有搜索条件，直接全选
    if (keyword) {
      tempSelectList = [...treeData]
    } else {
      // 否则去查询所有
      tempSelectList = currentTreeItem?.childs || []
      setState({ treeData: tempSelectList })
    }

    setState({
      selectedList: flag
        ? uniqueItems([...selectedList, ...tempSelectList])
        : removeData(selectedList, tempSelectList)
    })
  }

  useEffect(() => {
    const isAllSelected = currentTreeItem?.childs?.every((x: any) =>
      selectedList?.map((y: any) => y?.id)?.includes(x?.id)
    )
    setState({ allSelected: isAllSelected })
  }, [selectedList, currentTreeItem])

  useEffect(() => {
    setState({ selectedList: value || [] })
  }, [value])

  return (
    <HSearchView
      className='h-tree-search-view'
      mode={mode}
      count={allCount}
      allSelected={allSelected}
      selectedList={selectedList}
      onRemove={onRemove}
      onConfirm={onConfirm}
      onKeywordChange={onSearchChange}
      onAllSelectedChange={handleTreeItemChange}
      header={
        <>
          {breadcrumbList?.length > 1 && !keyword && (
            <div className='h-tree-select-breadcrumb'>
              {breadcrumbList?.map((item: any, index: any, arr: any) => {
                const isLast = index === arr.length - 1
                return (
                  <Fragment key={item?.name}>
                    <span
                      className={classNames('h-tree-select-breadcrumb-name', {
                        'h-tree-select-breadcrumb-name-last': isLast
                      })}
                      onClick={() => !isLast && handleOrgClick(item)}
                    >
                      {item?.name}
                    </span>
                    {index !== breadcrumbList.length - 1 && (
                      <RightOutline className='h-tree-select-breadcrumb-icon' />
                    )}
                  </Fragment>
                )
              })}
            </div>
          )}
        </>
      }
    >
      <HTreeNode
        data={treeData}
        loaded={loaded}
        originData={[...selectedList]}
        value={tempValue}
        mode={mode}
        keyword={keyword}
        type='org'
        needAuth={needAuth}
        showItemDesc
        onItemClick={handleOrgClick}
        onChange={handleTreeChange}
      />
    </HSearchView>
  )
}

export default observer(TreeSearchView)
